import { ArrowUpDown, FolderKanban, MoreHorizontal, PlusCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Link from "next/link";
import { cn, fetcherTyped, formatDistanceTime, okstatus } from "@/lib/utils";
import useSWR from "swr";
import React from "react";

import { AppShell } from "@/components/AppShell";
import { Host, Project } from "@/types";
import { menu } from "@/components/dashboard/Sidebar";
import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { ExternalLink, ObjectLink } from "@/components/OptionalLink";
import { DeleteAppDialog } from "@/components/app/DeleteAppDialog";
import { useLocalStorage } from "@/components/useLocalStorage";
import {
  AppListResponseSchema,
  AppListResponseType,
  AppWithHostSchemaType
} from "@/types/schemas";
import { formatMarkdown } from "@/components/formatMarkdown";

export const groupByProject = <T extends {}>(
  records?: (T & { project?: Pick<Project, "id" | "name" | "description"> | null })[]
) => {
  const projects: Record<
    string,
    Pick<Project, "id" | "name" | "description">
  > = Object.fromEntries(records?.map(app => [app.project?.id, app.project]) ?? []);

  const groups: Record<string, T[]> = {};

  records?.forEach(record => {
    const projectId = record.project?.id ?? "0";
    if (!groups[projectId]) {
      groups[projectId] = [];
    }
    groups[projectId].push(record as T);
  });

  return { projects, groups };
};

const AppControls = ({
  app,
  onDelete,
  onDuplicate
}: {
  app: AppWithHostSchemaType;
  onDelete?: (id: string) => void;
  onDuplicate?: () => void;
}) => {
  const router = useRouter();

  const handleDuplicate = () => {
    fetch(`/api/app/${app.id}/duplicate`, { method: "POST" })
      .then(okstatus)
      .then(res => res.json())
      .then(newApp => {
        if (onDuplicate) {
          onDuplicate();
        }
        router.push(`/app/${newApp.id}/edit`);
      });
  };

  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" asChild disabled={!app.hostname || app.status !== "active"}>
        <ExternalLink
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1"
          href={app?.hostname ? "https://" + app.hostname : "#"}
        >
          Launch
        </ExternalLink>
      </Button>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            aria-haspopup="true"
            size="icon"
            variant="ghost"
            className="flex items-center"
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <Link href={`/app/${app.id}`}>View</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={`/app/${app.id}/edit`}>Edit</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={`/journal?appId=${app.id}`}>Logs</Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate}>Duplicate</DropdownMenuItem>
          <DeleteAppDialog
            id={app.id}
            host={app.host}
            onDelete={() => {
              router.push("/app");
              if (onDelete) {
                onDelete(app.id);
              }
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Page = () => {
  const router = useRouter();
  const tab = (router.query.tab as string) ?? "all";
  const [orderBy, setOrderBy] = useLocalStorage<string>("app.order.by", "name");
  const [orderDesc, setOrderDesc] = useLocalStorage<boolean>("app.order.desc", false);
  const {
    data: apps,
    mutate,
    isValidating
  } = useSWR<AppListResponseType>(
    "/api/app?status=" + tab + "&orderBy=" + orderBy + "&orderDesc=" + orderDesc,
    fetcherTyped(AppListResponseSchema)
  );
  const { data: hosts } = useSWR<Host[]>("/api/host");
  const hostsCount = hosts?.length || 0;

  const handleAdd = useCallback(async () => {
    router.push("/app/create");
  }, [router]);

  const handleRefresh = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleOrderBy = useCallback(
    (orderBy: string) => {
      setOrderBy(orderBy);
      setOrderDesc(orderBy === orderBy && !orderDesc);
    },
    [orderDesc, setOrderBy, setOrderDesc]
  );

  const { projects, groups } = useMemo(() => groupByProject(apps), [apps]);

  return (
    <AppShell menuItem={menu.app}>
      <div className="mx-4 mb-20">
        <Card className="col-span-full gradient-header mt-4 py-4 mb-4">
          <CardHeader>
            <CardTitle className="text-xl">Applications</CardTitle>
            <CardDescription>Manage your applications deployed on your hosts</CardDescription>
          </CardHeader>
        </Card>

        {hostsCount > 0 ? (
          <Tabs value={tab} onValueChange={value => router.push("/app?tab=" + value)}>
            <div className="flex md:items-center justify-between md:flex-row flex-col gap-2">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="archived" className="hidden sm:flex">
                  Archived
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-8 gap-1 plausible-event-name=AddApp"
                  onClick={() => handleAdd()}
                >
                  <PlusCircle className="h-3.5 w-3.5" />
                  Add App
                </Button>

                <Button size="sm" className="h-8 gap-1" onClick={() => router.push("/r")}>
                  <PlusCircle className="mr-2 h-3.5 w-3.5 gap-1" /> One-click Deploy
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1"
                  onClick={e => {
                    e.preventDefault();
                    handleRefresh();
                  }}
                >
                  Refresh
                </Button>
              </div>
            </div>
            <TabsContent value={tab}>
              <Card>
                <CardContent>
                  <Table className="Responsive">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="md:pl-4">
                          <div className="flex items-center gap-1">
                            Name
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOrderBy("name")}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            Type
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOrderBy("type")}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            Status
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOrderBy("status")}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            Last Deployed
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOrderBy("lastDeployAt")}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className={cn(isValidating && "opacity-50")}>
                      {Object.entries(groups).map(([projectId, apps]) => (
                        <React.Fragment key={projectId}>
                          {projectId !== "0" || Object.keys(projects).length > 1 ? (
                            <TableRow className="transparent hover:transparent">
                              <TableCell colSpan={6}>
                                <div className="flex items-center gap-2 font-semibold text-lg py-2">
                                  <FolderKanban className="w-5 h-5" />
                                  {projects[projectId]?.name || "No project assigned"}
                                </div>

                                {projects[projectId]?.description ? (
                                  <div className="prose dark:prose-invert prose-sm line-clamp-2">
                                    {formatMarkdown(projects[projectId]?.description)}
                                  </div>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {apps.map(app => (
                            <TableRow
                              key={app.id}
                              className="cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => router.push(`/app/${app.id}`)}
                            >
                              <TableCell className="md:pl-2">
                                <ObjectLink
                                  href={`/app/${app.id}`}
                                  className="link"
                                  type="app"
                                >
                                  {app.name}
                                </ObjectLink>
                              </TableCell>
                              <TableCell data-label="Type">{app.type ?? "next"}</TableCell>
                              <TableCell data-label="Status">
                                <Badge>{app.status ?? "draft"}</Badge>
                              </TableCell>
                              <TableCell data-label="Last Deployed">
                                {app.lastDeployAt
                                  ? formatDistanceTime(app.lastDeployAt)
                                  : "never"}
                              </TableCell>
                              <TableCell data-label="Host">
                                <ObjectLink
                                  className="link"
                                  type="host"
                                  href={app.host ? `/host/${app?.host.id}` : undefined}
                                >
                                  {app.host?.name ?? "-"}
                                </ObjectLink>
                              </TableCell>
                              <TableCell
                                className="items-center gap-2 flex"
                                onClick={e => e.stopPropagation()}
                              >
                                <AppControls
                                  app={app}
                                  onDelete={handleRefresh}
                                  onDuplicate={handleRefresh}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}

                      {apps && apps.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center">
                            No apps found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
                <CardFooter>
                  <div className="text-xs text-muted-foreground">
                    Showing{" "}
                    <strong>
                      {apps && apps?.length > 0 ? 1 : 0}-{apps?.length}
                    </strong>{" "}
                    of <strong>{apps?.length}</strong> apps
                  </div>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <Card className="p-12 md:p-24">
            <CardContent className="flex flex-col justify-center  gap-4 text-center">
              To deploy your apps you will first need to connect a host (server) or create a
              new one with our integrated host providers.
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button onClick={() => router.push("/host")} variant="outline">
                View Hosts
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </AppShell>
  );
};

export default Page;
